import { AppBar, Badge, Box, Breadcrumbs, CircularProgress, Container, IconButton, Link, Menu, MenuItem, Stack, Toolbar, Tooltip, Typography } from "@mui/material";
import { Outlet, UIMatch, useMatches, useNavigate, Link as RLink, useLocation, Navigate, useNavigation } from "react-router";
import { createContext, useMemo, useState } from "react";
import { useConfirm } from "material-ui-confirm";
import { AccountCircle, PendingActions as PendingActionsIcon } from "@mui/icons-material";
import { MainRouteData } from "../routeFactory";
import { IApiService } from "../services/ApiService";
import { ListSkeleton } from "../pages/bailleur/logements/LogementsPage";
import { HomePageSkeleton } from "../pages/root/HomePageSkeleton";
import { SyncProgressSkeleton } from "../pages/root/SyncProgressSkeleton";
import { UnavailableSkeleton } from "../pages/root/UnavailableSkeleton";
import { Logger } from '../utils/Logger';
import { authNavigationService } from "../services/AuthNavigationService";
import { isSyncAffectedRoute } from "../utils/routeUtils";
import { useSWState } from "../contexts/SWStateContext";
import { useWakeUpRevalidate } from "../hooks/useWakeUpRevalidate";

export interface SubHeaderProps {
  readonly path: string;
}

export interface MainLayoutProps {
  readonly apiService: IApiService;
}

export interface SearchContext {
  readonly search: string;
  setSearch(value: string): void;
}

export const searchContext = createContext<SearchContext | undefined>(undefined);

/**
 * Maps navigation pathname to appropriate skeleton component.
 * Returns the skeleton to display for route-specific loading states.
 *
 * Note: For synced content with progress bar, use SyncProgressSkeleton
 * (handled in the main conditional via showProgressBar flag)
 */
const getSkeletonForPath = (pathname: string): React.ReactNode => {
  // Normalize pathname by removing trailing slash
  const normalizedPath = pathname.endsWith('/') && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;

  // Root path (/) -> HomePageSkeleton
  if (normalizedPath === '' || normalizedPath === '/') {
    return <HomePageSkeleton />;
  }

  // /bailleurs -> ListSkeleton
  if (normalizedPath === '/bailleurs') {
    return <ListSkeleton />;
  }

  // /{bailleurId} -> ListSkeleton (programmes list)
  // Pattern: /0, /1, /123, etc. (just digits)
  if (/^\/\d+$/.test(normalizedPath)) {
    return <ListSkeleton />;
  }

  // /{bailleurId}/logements or /{bailleurId}/logements/{programme} -> ListSkeleton
  // Pattern: /0/logements, /1/logements/Programme%20A, etc.
  if (/^\/\d+\/logements/.test(normalizedPath)) {
    return <ListSkeleton />;
  }

  // For other routes (logement detail pages, etc.), show centered spinner
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <CircularProgress />
    </Box>
  );
};

export const MainLayout = (props: MainLayoutProps) => {
  const currentLocation = useLocation();
  const { apiService } = props;

  // ✅ Recalculate isPublicRoute dynamically from current pathname
  // (Loader data may be stale when navigating between routes with same parent)
  const isPublicRoute = authNavigationService.isPublicRoute(currentLocation.pathname);

  Logger.debug('COMPONENT', "MainLayout rendering - pathname:", currentLocation.pathname, "isPublicRoute:", isPublicRoute);

  // Profile completion check: redirect incomplete profiles to account page
  // Uses apiService.isProfileComplete() which is synchronized during app init (via getUserDetail)
  if (apiService.isAuthenticated() && !isPublicRoute && currentLocation.pathname !== "/account/me") {
    if (!apiService.isProfileComplete()) {
      const returnUrl = currentLocation.pathname + currentLocation.search;
      const search = returnUrl ? `?returnUrl=${returnUrl}` : undefined;
      const to = {
        pathname: "/account/me",
        search: search,
      };
      return <Navigate to={to} replace />;
    }
  }

  const navigate = useNavigate();
  const navigation = useNavigation();
  const { state } = useSWState();
  useWakeUpRevalidate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };
  const confirm = useConfirm();

  const matches = useMatches() as UIMatch<MainRouteData, undefined | {
    subHeader?: (path: string) => React.FunctionComponent,
    crumb?: (path: string, data: MainRouteData | undefined) => ReadonlyArray<{path: string, title: string}>
  }>[];
  const crumbs: ReadonlyArray<{path: string, title: string}> = useMemo(() => {
    Logger.debug('COMPONENT', "MainLayout computing crumbs - matches count:", matches.length);
    const result = matches
    .reduce<ReadonlyArray<{path: string, title: string}>>((p, c) => {
      const crumb = c.handle?.crumb;
      if (!crumb) {
        return p;
      }
      return [...p, ...crumb(c.pathname, c.loaderData)];
    }, []);
    Logger.debug('COMPONENT', "MainLayout computed crumbs - count:", result.length);
    return result;
  }, [matches]);
  const subHeaders = useMemo(() => {
    return matches.reduce<ReadonlyArray<React.FunctionComponent>>((p, c) => {
      const subHeader = c.handle?.subHeader;
      if (!subHeader) {
        return p;
      }
      return [...p, subHeader(currentLocation.pathname)];
    }, []);
  }, [matches]);
  const header = <>
    <Toolbar sx={{ px: 1 }}>
        <Stack sx={{ flexGrow: 1 }}>
          <Breadcrumbs aria-label="Fil d'Ariane" sx={{ color: "primary.contrastText", fontSize: "0.8em" }}>
            {crumbs.slice(0, -1).map((x,i) => <CrumbLink key={i} link={x} />)}
          </Breadcrumbs>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography component="h1" variant="h6">
              {crumbs.slice(-1).map((x,i) => <CrumbLink key={i} link={x} />)}
            </Typography>
          </Stack>
        </Stack>
        {state.pendingCount > 0 && (
          <Tooltip title="Modifications en attente de synchronisation" enterTouchDelay={0} leaveTouchDelay={3000}>
            <Badge variant="dot" color="warning" sx={{ mr: 0.5 }}>
              <PendingActionsIcon />
            </Badge>
          </Tooltip>
        )}
        {apiService.isAuthenticated() && (
          <>
            <IconButton
              size="medium"
              aria-label="Utilisateur"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
              sx={{ flexGrow: 0}}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled={currentLocation.pathname === "/account/me"} onClick={() => {
                handleClose();
                navigate("/account/me");
              }}>Mon compte</MenuItem>
              <MenuItem onClick={() => {
                handleClose();
                if (state.pendingCount > 0) {
                  confirm({
                    title: "Modifications en attente",
                    description: "Des modifications n'ont pas encore été synchronisées. Se déconnecter maintenant pourrait entraîner une perte de données.",
                    confirmationText: "Se déconnecter",
                    cancellationText: "Annuler",
                    confirmationButtonProps: { variant: "outlined", color: "error" },
                    cancellationButtonProps: { variant: "outlined" },
                  }).then(() => navigate("/account/logout"))
                    .catch(() => { /* annulé */ });
                } else {
                  navigate("/account/logout");
                }
              }}>Se déconnecter</MenuItem>
            </Menu>
          </>
        )}
    </Toolbar>
  </>;
  const [search, setSearch] = useState("");

  return (
    <searchContext.Provider value={{
      search: search,
      setSearch: setSearch
    }}>
      <AppBar component="header" position="relative" sx={{visibility: "hidden", pt: 'env(safe-area-inset-top)'}} aria-hidden="true">
          {header}
          <Box sx={{p: 0, m:0, bgcolor: 'background.default'}}>
            <Container sx={{p: "1 0"}} maxWidth="md">
              {subHeaders.map((x, i) => <div key={i}>{x({})}</div>)}
            </Container>
          </Box>
      </AppBar>
      <AppBar component="header" position="fixed" sx={{p:0, pt: 'env(safe-area-inset-top)'}}>
          {header}
          <Box sx={{p: 0, m:0, bgcolor: 'background.default'}}>
            <Container sx={{p: "1 0"}} maxWidth="md">
              {subHeaders.map((x, i) => <div key={i}>{x({})}</div>)}
            </Container>
          </Box>
      </AppBar>
      <Container sx={{p: 0}} maxWidth={isPublicRoute ? "xs" : "md"}>
        {apiService.isAuthenticated() && (
          <>
            {(() => {
              const isSyncAffected = isSyncAffectedRoute(currentLocation.pathname);

              // Handle sync-affected routes with proper state-based rendering
              if (isSyncAffected) {
                switch (state.phase) {
                  case 'UNINITIALIZED':
                  case 'LOADING':
                    return getSkeletonForPath(currentLocation.pathname);

                  case 'SYNCING': {
                    const showProgressBar = state.logementsSync !== null && state.logementsSync.itemCount > 0;
                    return showProgressBar ? (
                      <SyncProgressSkeleton
                        syncCursorDate={state.logementsSync!.cursor}
                        minLogEntryCreatedDate={state.logementsSync!.min}
                      />
                    ) : (
                      getSkeletonForPath(currentLocation.pathname)
                    );
                  }

                  case 'UNAVAILABLE':
                    return <UnavailableSkeleton />;

                  case 'READY':
                  case 'SW_UPDATING':
                    if (navigation.state === "loading" && navigation.location) {
                      return getSkeletonForPath(navigation.location.pathname);
                    }
                    return <Outlet />;
                }
              }

              // Non-sync-affected routes: always show content (except during navigation)
              // These routes (like /account/me) are accessible even during sync
              if (navigation.state === "loading" && navigation.location) {
                return getSkeletonForPath(navigation.location.pathname);
              }
              return <Outlet />;
            })()}
          </>
        ) || (
          // Public route with no userDetail (not authenticated)
          <Outlet />
        )}
      </Container>
    </searchContext.Provider>
  );
};

interface CrumbLinkProps {
  link: {
      path: string,
      title: string
  }
}

const CrumbLink = (props: CrumbLinkProps): React.ReactNode => {
  const location = useLocation();
  const link = props.link;
  if (decodeURI(location.pathname) !== link.path) {
    return <Link
      component={RLink}
      to={link.path}
      color="inherit"
      underline="hover">
      {link.title}
    </Link>;
  } else {
    return <Link
    // component={RLink}
    // to={link.path}
    // href="#"
    color="inherit"
    underline="hover">
    {link.title}
  </Link>;
  }
}
